import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormControlLabel,
  Switch
} from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
}));

const ChatbotsSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  queueId: Yup.array().of(Yup.string()),
  chatbotEnabled: Yup.boolean(),
  chatbotTypes: Yup.boolean(),
  isAgent: Yup.boolean(),
  whatsappId: Yup.number().required("Selecciona una conexión"),
});

const BotModal = ({ open, onClose, botId }) => {
  const classes = useStyles();

  const initialState = {
    name: "",
    chatbotEnabled: 0,
    chatbotTypes: 0,
    isAgent: 0,
    queueId: [],
    whatsappId: 0,
  };

  const [bots, setBots] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);

  // Cargar conexiones de WhatsApp
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/whatsapp");
        setWhatsapps(data);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  // Cargar bot para edición
  useEffect(() => {
    (async () => {
      if (!botId) return;
      try {
        const { data } = await api.get(`/bots/${botId}`);
        setBots(prev => ({
          ...prev,
          ...data,
          chatbotTypes: data.chatbotTypes ? 1 : 0,
          whatsappId: data.whatsappId ?? 0,
        }));
        const userBotsIds = data.queues?.map(queue => queue.id) || [];
        setSelectedQueueIds(userBotsIds);
      } catch (err) {
        toastError(err);
      }
    })();

    return () => {
      setBots(initialState);
    };
  }, [botId, open]);

  // ⚠️ Si es Alertbot y seleccionó "Todos", se forza a otra conexión
  useEffect(() => {
    if (bots.chatbotTypes === 1 && bots.whatsappId === 0 && whatsapps.length > 0) {
      setBots(prev => ({
        ...prev,
        whatsappId: whatsapps[0].id
      }));
    }
  }, [bots.chatbotTypes, bots.whatsappId, whatsapps]);

  const handleClose = () => {
    onClose();
    setBots(initialState);
  };

  const handleSaveBots = async values => {
    const updatedValues = {
      ...values,
      queueId: selectedQueueIds,
      chatbotEnabled: values.chatbotEnabled ? 1 : 0,
      chatbotTypes: values.chatbotTypes === 1 ? 1 : 0,
      isAgent: values.isAgent ? 1 : 0,
    };
    try {
      if (botId) {
        await api.put(`/bots/${botId}`, updatedValues);
      } else {
        await api.post("/bots", updatedValues);
      }
      toast.success(i18n.t("botModal.success"));
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth scroll="paper">
        <DialogTitle>
          {botId
            ? i18n.t("botModal.title.edit")
            : i18n.t("botModal.title.add")}
        </DialogTitle>
        <Formik
          initialValues={bots}
          enableReinitialize={true}
          validationSchema={ChatbotsSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveBots(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Field
                  as={TextField}
                  label={i18n.t("botModal.form.name")}
                  autoFocus
                  name="name"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  variant="outlined"
                  margin="dense"
                  className={classes.textField}
                />

                <FormControlLabel
                  control={
                    <Field
                      as={Switch}
                      color="primary"
                      name="chatbotEnabled"
                      checked={values.chatbotEnabled || false}
                    />
                  }
                  label={i18n.t("botModal.form.chatbotEnabled")}
                />

                <FormControl
                  variant="outlined"
                  margin="dense"
                  className={classes.formControl}
                >
                  <InputLabel id="types-label">{i18n.t("botModal.form.types")}</InputLabel>
                  <Field
                    as={Select}
                    label={i18n.t("botModal.form.types")}
                    name="chatbotTypes"
                    labelId="types-label"
                    value={values.chatbotTypes}
                    onChange={e => setFieldValue("chatbotTypes", e.target.value)}
                  >
                    <MenuItem value={0}>TypeBots</MenuItem>
                    <MenuItem value={1}>Alertbot</MenuItem>
                  </Field>
                </FormControl>

                <FormControl
                  variant="outlined"
                  margin="dense"
                  className={classes.formControl}
                  fullWidth
                >
                  <InputLabel id="whatsappId-label">Conexión</InputLabel>
                  <Field
                    as={Select}
                    label="Conexión"
                    name="whatsappId"
                    labelId="whatsappId-label"
                    value={values.whatsappId}
                    onChange={(e) => setFieldValue("whatsappId", e.target.value)}
                  >
                    {/* Mostrar "Todos" solo si NO es Alertbot */}
                    {values.chatbotTypes === 0 && (
                      <MenuItem value={0}>Todos</MenuItem>
                    )}
                    {whatsapps.map((whatsapp) => (
                      <MenuItem key={whatsapp.id} value={whatsapp.id}>
                        {whatsapp.name}
                      </MenuItem>
                    ))}
                  </Field>
                </FormControl>

                <Field
                  as={QueueSelect}
                  selectedQueueIds={selectedQueueIds}
                  onChange={(values) => setSelectedQueueIds(values)}
                />

                <FormControlLabel
                  control={
                    <Field
                      as={Switch}
                      color="primary"
                      name="isAgent"
                      checked={values.isAgent || false}
                    />
                  }
                  label={i18n.t("botModal.form.isAgent")}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} color="secondary" disabled={isSubmitting} variant="outlined">
                  {i18n.t("botModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {botId
                    ? i18n.t("botModal.buttons.okEdit")
                    : i18n.t("botModal.buttons.okAdd")}
                  {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default BotModal;
